import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { authHeader, clearToken, isAuthed } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Star, Trash2, LogOut, Plus, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ n, onSet }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(i => (
      <button key={i} type="button" onClick={() => onSet && onSet(i)}
        className={`transition-colors ${onSet ? "cursor-pointer hover:scale-110" : ""}`}>
        <Star className={`w-5 h-5 ${i <= n ? "text-[#FF5722] fill-[#FF5722]" : "text-white/20"}`} />
      </button>
    ))}
  </div>
);

export default function AdminReviews() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [author, setAuthor] = useState("");
  const [quote, setQuote] = useState("");
  const [rating, setRating] = useState(5);
  const [source, setSource] = useState("Google");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthed()) { nav("/admin"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle401 = (err) => {
    if (err?.response?.status === 401) { clearToken(); nav("/admin"); return true; }
    return false;
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, { headers: authHeader() });
      setItems(data);
    } catch (err) { if (!handle401(err)) toast.error("Load failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!author || !quote) { toast.error("Author and quote required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/reviews`, { author, quote, rating, source }, { headers: authHeader() });
      setAuthor(""); setQuote(""); setRating(5); setSource("Google");
      toast.success("Review added");
      await load();
    } catch (err) {
      if (!handle401(err)) toast.error("Add failed");
    } finally { setBusy(false); }
  };

  const togglePublish = async (r) => {
    try {
      await axios.put(`${API}/admin/reviews/${r.id}`, { is_published: !r.is_published }, { headers: authHeader() });
      await load();
    } catch (err) { if (!handle401(err)) toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeader() });
      setItems(items.filter(i => i.id !== id));
      toast.success("Deleted");
    } catch (err) { if (!handle401(err)) toast.error("Delete failed"); }
  };

  const signOut = () => { clearToken(); nav("/admin"); };

  return (
    <div className="section px-6 lg:px-10" data-testid="admin-reviews-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow mb-3">Admin · Reviews</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Customer Reviews</h1>
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-zinc-400">
            <Link to="/admin/gallery" className="hover:text-[#FF5722] underline decoration-white/20">→ Gallery</Link>
            <span className="text-zinc-600">·</span>
            <Link to="/admin/quotes" className="hover:text-[#FF5722] underline decoration-white/20">→ Leads &amp; Messages</Link>
          </div>
          <p className="text-zinc-400 mt-3 max-w-2xl">Paste customer testimonials from Google, Facebook, or word-of-mouth. Published reviews appear on the homepage.</p>
        </div>
        <button onClick={signOut} className="btn-ghost" data-testid="reviews-signout"><LogOut className="w-4 h-4" /> Sign Out</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <form onSubmit={submit} className="lg:col-span-5 border border-white/10 p-6 bg-[#0B0B0D] h-fit space-y-4" data-testid="review-form">
          <div className="eyebrow flex items-center gap-2"><Plus className="w-4 h-4" /> New Review</div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Author (Name, Company)</Label>
            <Input value={author} onChange={e => setAuthor(e.target.value)} placeholder="e.g., Mike T., Riverside Fab" data-testid="review-author" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Quote</Label>
            <textarea value={quote} onChange={e => setQuote(e.target.value)} rows={4} placeholder="Paste the review text..."
              className="flex w-full rounded-none border border-white/20 bg-transparent px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              data-testid="review-quote" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Rating</Label>
            <StarRow n={rating} onSet={setRating} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Source</Label>
            <select value={source} onChange={e => setSource(e.target.value)}
              className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF5722]" data-testid="review-source">
              <option value="Google">Google</option>
              <option value="Facebook">Facebook</option>
              <option value="Yelp">Yelp</option>
              <option value="Instagram">Instagram</option>
              <option value="Word of Mouth">Word of Mouth</option>
            </select>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="review-submit">
            {busy ? "Adding..." : "Add Review"}
          </button>
        </form>

        <div className="lg:col-span-7 space-y-3">
          {items.length === 0 ? (
            <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500 text-sm">No reviews yet.</div>
          ) : items.map(r => (
            <div key={r.id} className={`border p-5 bg-[#0B0B0D] ${r.is_published ? "border-white/10" : "border-white/5 opacity-60"}`} data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <StarRow n={r.rating} />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{r.source}</span>
                    {!r.is_published && <span className="text-[10px] uppercase tracking-widest text-yellow-500">Hidden</span>}
                  </div>
                  <p className="text-zinc-200 text-sm leading-relaxed">{r.quote}</p>
                  <div className="font-display text-lg tracking-tight mt-3">{r.author}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => togglePublish(r)} className="p-2 text-zinc-400 hover:text-[#FF5722]" title={r.is_published ? "Hide from home" : "Show on home"} data-testid={`review-toggle-${r.id}`}>
                    {r.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => remove(r.id)} className="p-2 text-zinc-400 hover:text-[#FF5722]" title="Delete" data-testid={`review-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
